/**
 * Portal — B-2026-0056
 *
 * Renders children via `ReactDOM.createPortal` into a target DOM node
 * outside the component tree. This escapes stacking-context and z-index
 * conflicts, making it the correct foundation for overlay panels that must
 * float above arbitrary page content.
 *
 * Usage:
 * ```tsx
 * <Portal zIndex="z-[9000]">
 *   <div className="fixed inset-0 pointer-events-none">
 *     <div className="pointer-events-auto ...">Overlay card</div>
 *   </div>
 * </Portal>
 * ```
 *
 * Mount target resolution (in priority order):
 * 1. The element matched by `selector` prop (when provided).
 * 2. The element with id="portal-root" (if it exists in the document).
 * 3. `document.body` (universal fallback).
 *
 * The `zIndex` prop wraps the children in a `<div>` with the given Tailwind
 * z-index class (default `z-[9000]`) so callers don't need to add it
 * themselves — but it can be overridden or set to "" to skip.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface PortalProps {
  children: ReactNode;
  /**
   * Tailwind z-index class applied to the wrapper div.
   * @default "z-[9000]"
   */
  zIndex?: string;
  /**
   * Optional CSS selector for the mount target.
   * Falls back to #portal-root, then document.body.
   */
  selector?: string;
}

function Portal({ children, zIndex = 'z-[9000]', selector }: PortalProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);

  // Lazily create the wrapper div and append it to the resolved target.
  if (!mountRef.current) {
    mountRef.current = document.createElement('div');
    if (zIndex) {
      mountRef.current.className = zIndex;
    }
  }

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // Resolve mount target: explicit selector → #portal-root → document.body
    const target =
      (selector ? document.querySelector(selector) : null) ??
      document.getElementById('portal-root') ??
      document.body;

    target.appendChild(mount);

    return () => {
      if (target.contains(mount)) {
        target.removeChild(mount);
      }
    };
  }, [selector]);

  return createPortal(children, mountRef.current);
}

export default Portal;
